import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import DoneScreen from './DoneScreen';
import StepBrand from './StepBrand';
import StepProfile from './StepProfile';
import StepPrompts from './StepPrompts';
import { STEP_BRAND, STEP_DONE, STEP_PROFILE, STEP_PROMPTS } from './state';

/**
 * Runs the three setup steps as one uninterrupted stretch.
 *
 * Each step hands the next one a project that is one step further along, so nothing is re-read
 * from the server between them and the flow never pauses on a page the user did not ask for.
 * The project it is holding is also handed back on every exit, so whichever page opened the
 * flow can redraw its checklist without a round trip.
 *
 * Closing part-way is a first-class ending, not a failure: progress lives in the project itself,
 * so whatever a step managed to save is already saved, and reopening starts at the first thing
 * still missing.
 */
class OnboardingFlow extends Component {
    constructor(props) {
        super(props);

        this.onBrandSaved = this.onBrandSaved.bind(this);
        this.onProfiled = this.onProfiled.bind(this);
        this.onProjectUpdated = this.onProjectUpdated.bind(this);
        this.onProfileContinue = this.onProfileContinue.bind(this);
        this.onPromptsAdded = this.onPromptsAdded.bind(this);
        this.onGoToProject = this.onGoToProject.bind(this);
        this.onClose = this.onClose.bind(this);

        this.state = {
            step: props.initialStep ?? STEP_BRAND,
            project: props.project,
            promptCount: 0,
        };
    }

    /** Keep the host page's copy of the project in step with ours as each step changes it. */
    updateProject(project) {
        this.setState({ project });
        this.props.onProjectChanged?.(project);
    }

    /** A step saved the project itself -- the market it needed before it could run. */
    onProjectUpdated(project) {
        if (project) this.updateProject(project);
    }

    onBrandSaved(project) {
        this.updateProject(project);
        this.setState({ step: STEP_PROFILE });
    }

    /**
     * The profile is saved server-side the moment it is generated, so the project is already
     * one step further along than the copy we are holding. Marking it here keeps the checklist
     * honest even if the user closes on the preview instead of continuing.
     */
    onProfiled() {
        this.updateProject({ ...this.state.project, brand_profile_available: true });
    }

    onProfileContinue() {
        this.setState({ step: STEP_PROMPTS });
    }

    onPromptsAdded(data, body) {
        const prompts = body?.prompts ?? [];
        this.updateProject({
            ...this.state.project,
            // Enough for the checklist to count this step done; the real list arrives with the
            // host page's next fetch.
            prompts: [
                ...(this.state.project?.prompts ?? []),
                ...prompts.map((prompt) => ({ prompt })),
            ],
        });
        this.setState({
            step: STEP_DONE,
            promptCount: prompts.length,
        });
    }

    onGoToProject() {
        window.location.href = `/ai-tracking/project/${this.state.project?.id}/`;
    }

    onClose() {
        this.props.onClose?.(this.state.project);
    }

    render() {
        const { step, project } = this.state;

        if (!project) return null;

        return (
            <>
                {step === STEP_BRAND && (
                    <StepBrand
                        isOpen
                        project={project}
                        onSaved={this.onBrandSaved}
                        onClose={this.onClose}
                    />
                )}
                {step === STEP_PROFILE && (
                    <StepProfile
                        isOpen
                        project={project}
                        onProfiled={this.onProfiled}
                        onProjectUpdated={this.onProjectUpdated}
                        onContinue={this.onProfileContinue}
                        onClose={this.onClose}
                    />
                )}
                {step === STEP_PROMPTS && (
                    <StepPrompts
                        isOpen
                        project={project}
                        onAdded={this.onPromptsAdded}
                        onProjectUpdated={this.onProjectUpdated}
                        onClose={this.onClose}
                    />
                )}
                {step === STEP_DONE && (
                    <DoneScreen
                        isOpen
                        project={project}
                        promptCount={this.state.promptCount}
                        onGoToProject={this.onGoToProject}
                        onClose={this.onClose}
                    />
                )}
            </>
        );
    }
}

export default withTranslation()(OnboardingFlow);
